import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import Post from '../components/Post';
import PremiumBadge from '../components/PremiumBadge';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

import {
  Send, Image, X, TrendingUp, Calendar, Loader2, AlertCircle, Search, Smile,
  DollarSign, Bitcoin, Activity, Mail, Menu, User, Settings, Hash, Users,
  ChevronDown, ChevronUp
} from 'lucide-react';

// --- Constants (Emojis) ---
const commonEmojis = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌',
  '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗',
  '🤭', '🤫', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
  '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶',
  '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️',
  '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭',
  '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬',
  '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖',
  '❤️', '🧡', '💛', '💚', '💙', '🖤', '🤍', '🤎', '💔', '❣️', '💕',
  '💞', '💓', '💗', '💖', '💘', '💝', '👍', '👎', '👌', '✌️', '🤞',
  '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '👋',
  '🤙', '💪', '🙏'
];

const tradingEmojis = {
  forex: ['💱', '💵', '💴', '💶', '💷', '💸', '💰', '💲', '📈', '📉', '📊', '📋'],
  crypto: ['₿', '🪙', '💎', '🚀', '🌕', '🌑', '🌗', '🌘', '🌒', '🌓', '🌔', '🌖'],
  stocks: ['📈', '📉', '📊', '📋', '📌', '📍', '🏢', '🏬', '🏭', '🏛️', '🏦', '🏤'],
  commodities: ['🛢️', '⛽', '🔥', '⚡', '💎', '🥇', '🥈', '🥉', '🌾', '🌽', '🍞', '🥐']
};

const emojiTabs = [
  { key: 'general', label: 'General', icon: <Smile size={16} /> },
  { key: 'forex', label: 'Forex', icon: <DollarSign size={16} /> },
  { key: 'crypto', label: 'Crypto', icon: <Bitcoin size={16} /> },
  { key: 'stocks', label: 'Stocks', icon: <TrendingUp size={16} /> },
  { key: 'commodities', label: 'Commodities', icon: <Activity size={16} /> }
];

// Helper to fix Mixed Content URLs
const PROD_URL = 'https://edgehub.onrender.com';
const fixUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/')) return `${PROD_URL}${url}`;
  if (url.includes('localhost:8000')) return url.replace('http://localhost:8000', PROD_URL);
  return url;
};

// ============================================================
// ✅ SECURITY HELPER: SANITIZE INPUT
// ============================================================
const sanitizeInput = (input) => {
  if (!input) return "";
  return String(input)
    .replace(/</g, "&lt;") // Escape HTML brackets
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

// --- NEW: Helper Hook for Polling ---
function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export default function CommunityPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser: currentUserFromContext } = useOutletContext();
  const { currentUser: currentUserFromAuth, refreshUser } = useAuth();

  // --- STATE MANAGEMENT ---
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form & UI State
  const [newPostText, setNewPostText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [posting, setPosting] = useState(false);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState({ users: [], posts: [] });
  
  // Emoji & UI State
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeEmojiTab, setActiveEmojiTab] = useState('general');
  const [processingLikeId, setProcessingLikeId] = useState(null);
  const [processingFollowId, setProcessingFollowId] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [showAllTrending, setShowAllTrending] = useState(false);

  // ✅ NEW: Message Notification State
  const [unreadCount, setUnreadCount] = useState(0);

  // Mention State
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionResults, setMentionResults] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);

  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  const currentUser = currentUserFromContext || currentUserFromAuth;

  // --- CONSTANTS ---
  const allTags = [
    '#BullMarket', '#Crypto', '#Forex', '#Fed', '#Oil', 
    '#Gold', '#Trading', '#PriceAction', '#TechnicalAnalysis', '#DayTrading', 
    '#MarketNews', '#Smc', '#Ict', '#SwingTrading'
  ];
  
  const visibleTags = showAllTrending ? allTags : allTags.slice(0, 4);

  // --- DATA FETCHING ---

  // ✅ NEW: Fetch Unread Message Count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/messages/conversations', { params: { tab: 'chats' } });
      const data = response.data || [];
      const total = data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadCount(total);
    } catch (err) {
      console.error("Could not fetch unread count", err);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/community/posts');
      const data = response.data?.items || response.data || [];
      setPosts(data);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
      setError(err.message || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  const containerRef = usePullToRefresh(fetchPosts);

  // --- EFFECTS ---

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchPosts();
    fetchUnreadCount();
  }, [currentUser, navigate, fetchPosts, fetchUnreadCount]);

  // Scroll to post from notification click
  useEffect(() => {
    const postId = searchParams.get('postId');
    if (postId && posts.length > 0) {
      const postElement = document.getElementById(`post-${postId}`);
      if (postElement) {
        setTimeout(() => {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
        // Remove the query param after scrolling
        setSearchParams({});
      }
    }
  }, [searchParams, posts, setSearchParams]);

  useInterval(() => {
    if (currentUser) {
      fetchUnreadCount();
    }
  }, 30000);

  // ✅ SECURITY: Sanitize Search Query before using
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery.trim()) {
      setSearchResults({ users: [], posts: [] });
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // We send the raw query but ensure it's a string
        const response = await api.get('/community/search', { params: { q: searchQuery } });
        setSearchResults({ users: response.data.users || [], posts: response.data.posts || [] });
      } catch (err) {
        console.error("Search failed:", err);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  // --- HANDLERS ---

  const handleLike = useCallback(async (postId) => {
    if (processingLikeId) return;
    setProcessingLikeId(postId);
    try {
      const post = posts.find(p => p.id === postId);
      const hasLiked = post?.has_liked || post?.hasLiked;
      if (hasLiked) await api.delete(`/community/posts/${postId}/like`);
      else await api.post(`/community/posts/${postId}/like`);
      fetchPosts();
    } catch (err) {
      console.error("Failed to toggle like", err);
    } finally {
      setProcessingLikeId(null);
    }
  }, [processingLikeId, posts, fetchPosts]);

  const handleAddComment = useCallback(async (postId, content, parentId = null) => {
    // ✅ SECURITY: Sanitize comment content
    const safeContent = sanitizeInput(content);
    try {
      await api.post(`/community/posts/${postId}/comments`, { content: safeContent, parent_id: parentId });
      fetchPosts();
    } catch (err) {
      console.error("Failed to add comment", err);
    }
  }, [fetchPosts]);

  const handleDeletePost = useCallback(async (postId) => {
    try {
      await api.delete(`/community/posts/${postId}`);
      fetchPosts();
    } catch (err) {
      console.error("Delete failed", err);
      alert(err?.response?.data?.detail || "Failed to delete the post. Please try again.");
    }
  }, [fetchPosts]);

  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await api.delete(`/community/comments/${commentId}`);
      fetchPosts();
    } catch (err) {
      console.error("Failed to delete comment", err);
      alert(err?.response?.data?.detail || "Failed to delete the comment. Please try again.");
    }
  }, [fetchPosts]);

  const handleRepost = useCallback(async (postId) => {
    try { await api.post(`/community/posts/${postId}/repost`); fetchPosts(); } 
    catch (err) { console.error("Repost failed", err); }
  }, [fetchPosts]);

  const handleUnrepost = useCallback(async (postId) => {
    try { await api.delete(`/community/posts/${postId}/repost`); fetchPosts(); } 
    catch (err) { console.error("Unrepost failed", err); }
  }, [fetchPosts]);

  const handleFollowUser = useCallback(async (userOrId) => {
    const userId = typeof userOrId === 'object' ? userOrId.id : userOrId;
    if (!userId || processingFollowId === userId) return;
    setProcessingFollowId(userId);
    
    setPosts(prevPosts => prevPosts.map(p => {
      if (p.user_id === userId) return { ...p, is_following_author: true, followers_count: (p.followers_count || 0) + 1 };
      return p;
    }));
    try {
      await api.post(`/community/follow/${userId}`);
      await refreshUser();
      fetchPosts();
    } catch (err) {
      console.error("Follow failed", err);
      setPosts(prevPosts => prevPosts.map(p => {
        if (p.user_id === userId) return { ...p, is_following_author: false, followers_count: Math.max(0, (p.followers_count || 1) - 1) };
        return p;
      }));
    } finally {
      setProcessingFollowId(null);
    }
  }, [processingFollowId, fetchPosts, refreshUser]);

  const handleUnfollowUser = useCallback(async (userOrId) => {
    const userId = typeof userOrId === 'object' ? userOrId.id : userOrId;
    if (!userId || processingFollowId === userId) return;
    setProcessingFollowId(userId);
    
    setPosts(prevPosts => prevPosts.map(p => {
      if (p.user_id === userId) return { ...p, is_following_author: false, followers_count: Math.max(0, (p.followers_count || 1) - 1) };
      return p;
    }));
    try {
      await api.delete(`/community/follow/${userId}`);
      await refreshUser();
      fetchPosts();
    } catch (err) {
      console.error("Unfollow failed", err);
      setPosts(prevPosts => prevPosts.map(p => {
        if (p.user_id === userId) return { ...p, is_following_author: true, followers_count: (p.followers_count || 0) + 1 };
        return p;
      }));
    } finally {
      setProcessingFollowId(null);
    }
  }, [processingFollowId, fetchPosts, refreshUser]);

  const handleMessageUser = useCallback((userId) => navigate(`/dashboard/messages/${userId}`), [navigate]);

  const handleReportPost = useCallback(async (postId) => {
    const reason = window.prompt("Please enter the reason for reporting this post:");
    if (reason === null) return;
    if (!reason || !reason.trim()) { alert("Please provide a reason for the report."); return; }
    
    // ✅ SECURITY: Sanitize report reason
    const safeReason = sanitizeInput(reason.trim());
    
    try {
      await api.post(`/community/posts/${postId}/report`, { reason: safeReason });
      alert("Post reported successfully.");
    } catch (err) {
      if (err.response?.status === 400) alert("You have already reported this post.");
      else alert("Failed to submit report.");
    }
  }, []);

  const handleUserClick = (userId) => {
    if (!userId) return;
    navigate(`/dashboard/profile/${userId}`);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPostText.trim() && !selectedImage) return;
    setPosting(true);
    try {
      const formData = new FormData();
      // ✅ SECURITY: Sanitize post content
      formData.append('content', sanitizeInput(newPostText));
      if (selectedImage) formData.append('images', selectedImage);
      
      await api.post('/community/posts', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      
      setNewPostText('');
      setSelectedImage(null);
      setImagePreview(null);
      setShowPostForm(false);
      setShowEmojiPicker(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchPosts();
    } catch (error) {
      console.error('❌ ERROR creating post:', error);
      alert("Failed to create post.");
    } finally {
      setPosting(false);
    }
  };

  const searchUsersForMention = async (query) => {
    if (!query) { setMentionResults([]); return; }
    try {
      const res = await api.get('/community/users/search', { params: { q: query } });
      setMentionResults(res.data.users || []);
    } catch (err) {
      console.error("Mention search failed", err);
    }
  };

  const handlePostTextChange = (e) => {
    const text = e.target.value;
    const cursor = e.target.selectionStart;
    setNewPostText(text);
    setCursorPosition(cursor);

    const textBeforeCursor = text.substring(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        const query = textAfterAt;
        setMentionQuery(query);
        setShowMentions(true);
        searchUsersForMention(query);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleSelectMention = (user) => {
    const textBeforeCursor = newPostText.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const beforeMention = newPostText.substring(0, lastAtIndex);
    const tagName = user.name.toLowerCase().replace(/\s/g, ''); 
    const afterCursor = newPostText.substring(cursorPosition);
    const newText = `${beforeMention}@${tagName} ${afterCursor}`;
    setNewPostText(newText);
    setShowMentions(false);
    setMentionResults([]);
  };

  const handleEmojiSelect = (emoji) => setNewPostText(prev => prev + emoji);

  // --- HELPERS ---

  const normalizePost = useCallback((post) => {
    if (!post) return null;
    const isRepost = post.is_repost || post.isRepost || false;
    const base = {
      id: post.id,
      content: post.content,
      image_url: fixUrl(post.image_url || post.image),
      authorName: post.author_name || post.authorName,
      authorAvatar: fixUrl(post.author_avatar || post.authorAvatar),
      userId: post.user_id || post.userId,
      authorId: post.user_id || post.authorId,
      isAuthorPremium: post.author_is_premium || post.isAuthorPremium || false,
      is_following_author: post.is_following_author || false,
      likes_count: post.likes_count || post.like_count || 0,
      comments_count: post.comments_count || post.comment_count || 0,
      reposts_count: post.reposts_count || post.repost_count || 0,
      has_liked: post.has_liked || false,
      has_reposted: post.has_reposted || false,
      created_at: post.created_at,
      comments: post.comments || [],
      // Repost-row identity: lets the reposter delete their repost and see accurate state
      repostId: post.repost_id || post.repostId || null,
      reposterId: post.reposter_id || post.reposterId || null,
    };
    if (isRepost) return { ...base, isRepost: true, repostedBy: post.reposted_by || post.repostedBy || null };
    return { ...base, isRepost: false, repostedBy: null };
  }, []);

  const renderUserAvatar = (user, className = 'w-10 h-10') => {
    const avatarUrl = fixUrl(user?.avatar || user?.author_avatar);
    const userName = user?.name || user?.author_name || 'U';
    if (avatarUrl) return <img src={avatarUrl} alt={userName} className={`${className} rounded-full object-cover`} />;
    return (
      <div className={`${className} bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center`}>
        <span className="text-white font-bold">{userName ? userName[0].toUpperCase() : 'U'}</span>
      </div>
    );
  };

  const renderBadge = (isPremium, size = "w-5 h-5") => {
    if (!isPremium) return null;
    return <PremiumBadge size={16} className={size} />;
  };

  if (!currentUser) return null;

  return (
    <div 
      ref={containerRef}
      className="h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 max-w-7xl mx-auto pb-20 transition-colors duration-300 overflow-y-auto overflow-x-hidden"
    >
      
      {/* Sticky header with hamburger dropdown (mobile) — hero block scrolls beneath */}
      <header className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 pt-12 md:pt-3 pb-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Trading Community</h1>
          <div className="relative md:hidden">
            {/* Hamburger Button with Glow */}
            <button
              onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
              className="relative text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Menu size={24} strokeWidth={2} />

              {unreadCount > 0 && (
                <>
                  {/* Ping Animation */}
                  <span className="absolute top-1 right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                  </span>
                </>
              )}
            </button>

            {/* Dropdown menu (same pattern as the notifications bell) */}
            {isSidePanelOpen && (
              <div className="absolute top-12 right-0 w-72 max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 rounded-xl shadow-xl z-50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col items-center">
                    <div className="relative mb-2">
                      {renderUserAvatar(currentUser, 'w-14 h-14')}
                      {currentUser.is_premium && ( <div className="absolute -bottom-1 -right-1 z-10"><PremiumBadge size={16} className="w-5 h-5" /></div> )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{currentUser.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{currentUser.email}</p>
                  </div>
                </div>
                <nav className="p-2 space-y-1">
                  <button onClick={() => { navigate('/dashboard/profile/me'); setIsSidePanelOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors"><User size={18} /> <span className="font-medium">My Profile</span></button>
                  
                  {/* Messages Button with Count */}
                  <button 
                    onClick={() => { navigate('/dashboard/messages'); setIsSidePanelOpen(false); }} 
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <Mail size={18} /> 
                      <span className="font-medium">Messages</span>
                    </div>
                    {unreadCount > 0 && (
                      <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                        {unreadCount}
                      </span>
                    )}
                  </button>

                  <button onClick={() => { navigate('/dashboard/settings'); setIsSidePanelOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors"><Settings size={18} /> <span className="font-medium">Settings</span></button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero block (scrolls away) */}
      <div className="flex flex-col items-center px-4 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-4">
            {currentUser.is_premium && ( <div className="mt-2"><PremiumBadge size={28} className="w-8 h-8" /></div> )}
            <h1 className="text-2xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-500 tracking-tight text-center">Trading Community</h1>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-center max-w-2xl text-sm md:text-lg leading-relaxed">Share insights, discuss strategies, connect with fellow traders</p>
      </div>

      <div className="space-y-8">
        {/* Search Section */}
        <section className="relative">
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              placeholder="Search users or #hashtags..." 
              className="w-full bg-white dark:bg-gray-800/60 text-gray-900 dark:text-white rounded-xl px-4 py-3 pl-12 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-gray-200 dark:border-gray-600/50 shadow-sm" 
            />
            {isSearching && ( <div className="absolute right-4 top-1/2 transform -translate-y-1/2"><Loader2 size={18} className="animate-spin text-blue-500" /></div> )}
          </div>
        </section>

        {/* Trending Section */}
        <section>
          <div className="bg-white dark:bg-gray-800/40 backdrop-blur-lg rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><TrendingUp size={20} className="text-purple-500" />Trending Topics</h2>
            <div className="flex flex-wrap gap-2">
              {visibleTags.map((tag, index) => (
                <button key={index} onClick={() => setSearchQuery(tag)} className="px-3 py-2 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 rounded-full text-sm hover:bg-gray-200 dark:hover:bg-gray-600/50 transition-colors flex items-center gap-1"><Hash size={12} /> {tag.replace('#', '')}</button>
              ))}
            </div>
            {allTags.length > 5 && (
              <button onClick={() => setShowAllTrending(!showAllTrending)} className="mt-4 flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors">
                {showAllTrending ? <>Show Less <ChevronUp size={16} /></> : <>Show More <ChevronDown size={16} /></>}
              </button>
            )}
          </div>
        </section>

        {/* FAB */}
        <button onClick={() => setShowPostForm(!showPostForm)} className="fixed bottom-36 right-6 z-40 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full p-4 shadow-lg"><Send size={24} /></button>

        {/* Post Modal */}
        {showPostForm && (
          <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/40 dark:bg-black/50" onClick={() => setShowPostForm(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-t-2xl p-6 w-full max-w-lg mb-20 shadow-2xl border border-gray-200 dark:border-transparent" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Post</h2>
                <button onClick={() => setShowPostForm(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white"><X size={24} /></button>
              </div>

              <form onSubmit={handleCreatePost} className="space-y-4">
                {imagePreview && (
                  <div className="relative mb-4">
                    <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-xl" />
                    <button type="button" onClick={() => { setSelectedImage(null); setImagePreview(null); }} className="absolute top-2 right-2 bg-red-500/80 text-white p-2 rounded-full hover:bg-red-600"><X size={16} /></button>
                  </div>
                )}

                <div className="relative">
                  <textarea
                    value={newPostText}
                    onChange={handlePostTextChange}
                    placeholder="Share an insight... use @ to tag"
                    className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none border border-gray-200 dark:border-transparent"
                    rows={4}
                  />
                  <div className="absolute right-2 bottom-2 flex gap-2">
                    <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-2 text-gray-400 hover:text-yellow-500"><Smile size={20} /></button>
                    <button type="button" onClick={() => fileInputRef.current.click()} className="p-2 text-gray-400 hover:text-blue-500"><Image size={20} /></button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </div>

                  {showMentions && mentionResults.length > 0 && (
                    <div className="absolute z-50 bottom-full mb-2 left-0 w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 max-h-40 overflow-y-auto">
                      {mentionResults.map(user => (
                        <button
                          type="button"
                          key={user.id}
                          onClick={() => handleSelectMention(user)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                        >
                          {renderUserAvatar(user, 'w-8 h-8')}
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">{user.name}</p>
                            <p className="text-xs text-gray-500">@{user.username || user.name.toLowerCase()}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {showEmojiPicker && (
                  <div className="bg-gray-50 dark:bg-gray-800/90 rounded-2xl p-4 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700/50 shadow-2xl">
                    <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 dark:border-gray-700/50 pb-2">
                      {emojiTabs.map(({ key, label, icon }) => (
                        <button key={key} type="button" onClick={() => setActiveEmojiTab(key)} className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1 ${activeEmojiTab === key ? 'bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700/50'}`}>
                          {icon} {label}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-8 sm:grid-cols-10 gap-1">
                      {(activeEmojiTab === 'general' ? commonEmojis : tradingEmojis[activeEmojiTab] || []).map((emoji, index) => (
                        <button key={index} type="button" onClick={() => handleEmojiSelect(emoji)} className="text-lg sm:text-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl p-1.5 sm:p-2 min-w-0">{emoji}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">{newPostText.length}/280</span>
                  <button type="submit" disabled={posting || (!newPostText.trim() && !selectedImage)} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium py-3 px-6 rounded-xl flex items-center gap-2 disabled:opacity-50 shadow-md">
                    {posting ? <><Loader2 size={20} className="animate-spin" /> Posting...</> : <><Send size={20} /> Post</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* FEED SECTION */}
        <section>
          <div className="space-y-6">
            {loading && posts.length === 0 && ( <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div> )}
            {error && ( <div className="text-center py-12"><AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" /><p className="text-gray-900 dark:text-white text-lg mb-2">Failed to load posts</p><button onClick={() => fetchPosts()} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Try Again</button></div> )}

            {/* SEARCH RESULTS VIEW */}
            {!loading && searchQuery.trim() && (
              <>
                {searchResults.users.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><Users size={18} /> Users</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {searchResults.users.map(user => (
                        <div key={user.id} onClick={() => handleUserClick(user.id)} className="flex items-center gap-3 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md transition-all">
                          {renderUserAvatar(user, 'w-12 h-12')}
                          <div className="flex-1">
                            <div className="flex items-center gap-2"><p className="font-bold text-gray-900 dark:text-white">{user.name}</p>{user.is_premium && <PremiumBadge size={14} className="w-4 h-4" />}</div>
                            {user.username && <p className="text-xs text-gray-500">@{user.username}</p>}
                            <p className="text-xs text-gray-500">View Profile</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.posts.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><Search size={18} /> Posts</h3>
                    {searchResults.posts.map(post => {
                       const normalizedPost = normalizePost(post);
                       if (!normalizedPost) return null;
                       const isOwnPost = String(currentUser?.id) === String(normalizedPost.userId) || (normalizedPost.reposterId != null && String(currentUser?.id) === String(normalizedPost.reposterId));
                       const highlightPostId = searchParams.get('postId');
                       return <Post key={normalizedPost.id} post={normalizedPost} currentUser={currentUser} onLike={handleLike} onAddComment={handleAddComment} onDelete={handleDeletePost} onDeleteComment={handleDeleteComment} onRepost={handleRepost} onUnrepost={handleUnrepost} onReport={handleReportPost} onFollowUser={handleFollowUser} onUnfollowUser={handleUnfollowUser} isFollowingAuthor={normalizedPost.is_following_author} onUserClick={handleUserClick} onMessageUser={handleMessageUser} isProcessingLike={processingLikeId === normalizedPost.id} isOwnPost={isOwnPost} renderBadge={renderBadge} highlight={String(normalizedPost.id) === String(highlightPostId)} />;
                    })}
                  </div>
                )}
                {!isSearching && searchResults.users.length === 0 && searchResults.posts.length === 0 && (
                  <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                    <Search size={48} className="text-gray-300 dark:text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No results found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">No users or posts match "<span className="font-medium">{searchQuery}</span>"</p>
                  </div>
                )}
              </>
            )}

            {/* NORMAL FEED VIEW */}
            {!loading && !searchQuery.trim() && (
              <>
                {posts.length === 0 ? (
                  <div className="text-center py-12"><Calendar size={48} className="text-gray-300 dark:text-gray-400 mx-auto mb-4" /><p className="text-gray-400 text-lg">No posts yet</p></div>
                ) : (
                  posts.map(post => {
                    const normalizedPost = normalizePost(post);
                    if (!normalizedPost) return null;
                    const isOwnPost = String(currentUser?.id) === String(normalizedPost.userId) || (normalizedPost.reposterId != null && String(currentUser?.id) === String(normalizedPost.reposterId));
                    const highlightPostId = searchParams.get('postId');
                    return <Post key={normalizedPost.isRepost ? `${normalizedPost.id}-repost` : normalizedPost.id} post={normalizedPost} currentUser={currentUser} onLike={handleLike} onAddComment={handleAddComment} onDelete={handleDeletePost} onDeleteComment={handleDeleteComment} onRepost={handleRepost} onUnrepost={handleUnrepost} onReport={handleReportPost} onFollowUser={handleFollowUser} onUnfollowUser={handleUnfollowUser} isFollowingAuthor={normalizedPost.is_following_author} onUserClick={handleUserClick} onMessageUser={handleMessageUser} isProcessingLike={processingLikeId === normalizedPost.id} isOwnPost={isOwnPost} renderBadge={renderBadge} highlight={String(normalizedPost.id) === String(highlightPostId)} />;
                  })
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
